import React from "react";
import {Reservation} from "types/entities/Reservation";
import {useTranslation} from "react-i18next";
import {closeModal, openModal} from "helpers/actions";
import {ModalSize} from "pages/Front/components/Popup";
import CreditsBalanceModal from "pages/Front/Booking/BookingLogin/components/CreditsBalance/CreditsBalanceModal";
import {useDispatch} from "useDispatch";
import clsx from "clsx";
import FeatherIcon from "components/FeatherIcon";
import {PackageItemItemType} from "types/enums/PackageItemItemType";
import {PackageCreditItemType} from "types/enums/PackageCreditItemType";

interface CreditsBalanceInfoCardProps {
    reservation: Reservation | null;
    className?: string;
}

const CreditsBalanceInfoCard = (props: CreditsBalanceInfoCardProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const creditItems = props.reservation?.packagesCart?.packagesCartItems?.filter(item => item?.packageItem?.type === PackageItemItemType.Credit);
    const familyCreditItem = creditItems?.find(item => item?.packageItem?.article?.creditPackage?.category === PackageCreditItemType.Family);
    const hasFamilyCreditItem = familyCreditItem !== undefined;
    const credits = familyCreditItem?.packageItem?.article?.creditPackage?.amount_total || 0;
    const totalDrivers = props.reservation?.persons || 0;
    const assignedDrivers = props.reservation?.reservationDrivers?.filter(rd => rd.driver_id !== null).length || 0;
    const isFull = totalDrivers > 0 && assignedDrivers === totalDrivers;
    const reservationDrivers = props.reservation?.reservationDrivers ?? [];
    const driversWithLoadedLimits = reservationDrivers.filter(driver => driver.credit_limit !== undefined);
    const driversWithLimit = driversWithLoadedLimits.filter(driver => typeof driver.credit_limit === "number").length;
    const driversWithoutLimit = driversWithLoadedLimits.filter(driver => driver.credit_limit === null).length;
    const hasActiveCreditLimits = driversWithLimit > 0;

    const handleOpenModal = () => {
        dispatch(
            openModal(<CreditsBalanceModal reservation={props.reservation} />, {
                modalSize: ModalSize.Scrollable,
                closeModal: () => {
                    dispatch(closeModal());
                },
            })
        );
    };

    if (!hasFamilyCreditItem) {
        return <></>;
    }

    if (hasActiveCreditLimits) {
        return (
            <div
                className={clsx(
                    "CreditsBalanceInfoCard rounded-[10px] border-2 border-arbo-red bg-white p-4 md:p-6 lg:p-[30px] flex flex-col md:flex-row gap-4 md:gap-6 lg:gap-8 text-black items-start md:items-center justify-between min-h-[150px]",
                    props?.className
                )}
            >
                <div className={"flex flex-col xs:flex-row items-center justify-between gap-4 lg:gap-8"}>
                    <div className={"flex flex-row gap-2 lg:gap-[25px] items-center"}>
                        <img src="/img/credits/arbo-credits-icon.svg" alt={t("credits icon")} className="size-[40px] md:size-[56px] lg:size-[90px] flex-none" />
                        <div className={"flex flex-col gap-1 items-center justify-center"}>
                            <span className={"text-base md:text-lg lg:text-2xl lg:text-[40px] font-bold leading-none"}>{credits}</span>
                            <span className={"text-sm md:text-base lg:text-lg font-bold leading-none"}>{t("Credits")}</span>
                        </div>
                    </div>
                    <div className={"w-px self-stretch bg-arbo-red hidden md:block"}></div>
                    <div className={"flex flex-col gap-3 md:gap-4 min-w-0"}>
                        <div className={"flex flex-col gap-1"}>
                            <h5 className={"text-sm md:text-base lg:text-lg font-bold"}>{t("Credit limits active")}</h5>
                            <div className={"text-xs md:text-sm lg:text-base font-semibold text-black"}>
                                {t("{{driversWithLimit}} with Limit, {{driversWithoutLimit}} without Limit", {
                                    driversWithLimit,
                                    driversWithoutLimit,
                                })}
                            </div>
                        </div>
                        <div className={"text-xs md:text-sm lg:font-semibold text-black"}>
                            <p>{t("You can change the limits anytime, also in Park")}</p>
                        </div>
                    </div>
                </div>

                <div className={"w-full sm:w-auto md:shrink-0 flex flex-col items-start md:items-end justify-center gap-1 md:gap-2"}>
                    <button
                        className={
                            "w-full sm:w-auto flex flex-row items-center justify-center gap-2 bg-white px-4 py-2 rounded border-[2px] border-black text-black text-sm font-bold hover:text-arbo-red focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-arbo-red focus-visible:ring-offset-2"
                        }
                        type={"button"}
                        onClick={handleOpenModal}
                    >
                        <FeatherIcon className={"size-[20px] lg:size-[24px] flex-none"} name={"sliders"} />
                        <span>{t("Ändern")}</span>
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            className={clsx(
                "CreditsBalanceInfoCard rounded-[10px] border-2 border-arbo-red border-dashed p-2 md:p-4 lg:p-[30px] flex flex-col md:flex-row gap-4 lg:gap-8 text-black items-center justify-between",
                isFull ? "bg-light-grey" : "bg-white",
                props?.className
            )}
        >
            <div className={"flex flex-col xs:flex-row items-center justify-between gap-4 lg:gap-8"}>
                <div className={"flex flex-row gap-2 lg:gap-[25px] items-center"}>
                    <img src="/img/credits/arbo-credits-icon.svg" alt={t("credits icon")} className="size-[40px] md:size-[56px] lg:size-[90px] flex-none" />
                    <div className={"flex flex-col gap-1 items-center justify-center"}>
                        <span className={"text-base md:text-lg lg:text-2xl lg:text-[40px] font-bold leading-none"}>{credits}</span>
                        <span className={"text-sm md:text-base lg:text-lg font-bold leading-none"}>{t("Credits")}</span>
                    </div>
                </div>
                <div className={"w-px self-stretch bg-arbo-red hidden md:block"}></div>
                <div className={"flex flex-col gap-2"}>
                    <h5 className={"text-sm md:text-base lg:text-lg font-bold"}>{t("Your shared family balance")}</h5>
                    <div className={"text-xs md:text-sm lg:font-semibold"}>
                        <p>{t("Everyone uses the credits automatically together — you don't have to do anything.")}</p>
                        <p>
                            <span className={"font-semibold lg:font-bold"}>{t("Optional: Credit limits per person")}</span>,{" "}
                            {t("as soon as all tickets are assigned — now or later in the park.")}
                        </p>
                    </div>
                </div>
            </div>
            <div className={"w-full sm:w-auto md:shrink-0 flex flex-col items-center justify-center gap-1 md:gap-2 lg:gap-3"}>
                {isFull ? (
                    <button
                        className={
                            "w-full sm:w-auto flex flex-row items-center justify-center gap-2 bg-white px-4 py-2 rounded border-[2px] border-black text-black text-sm font-bold hover:text-arbo-red focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-arbo-red focus-visible:ring-offset-2"
                        }
                        type={"button"}
                        onClick={handleOpenModal}
                    >
                        <FeatherIcon className={"size-[20px] lg:size-[24px] flex-none"} name={"sliders"} />
                        <span>{t("Set credit limits · optional")}</span>
                    </button>
                ) : (
                    <>
                        <div className={"flex flex-row items-center gap-2"}>
                            <FeatherIcon className={"size-[20px] lg:size-[24px] flex-none"} name={"lock"} />
                            <span className={"font-bold text-sm md:text-lg lg:text-xl leading-none"}>
                                {t("{{assignedDrivers}} of {{totalDrivers}}", {assignedDrivers, totalDrivers})}
                            </span>
                        </div>
                        <div className={"text-xs md:text-sm font-semibold"}>{t("Tickets assigned")}</div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CreditsBalanceInfoCard;
